import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from 'antd';
import OwnerPersonalInfoCards from './cards/OwnerPersonalInfoCards';
import PetsittingAddressInfoCards from './cards/PetsittingAddressInfoCards';
import AnimalInfoCards from './cards/AnimalInfoCards';
import { RatingInfoCards } from './cards/RatingInfoCards';
import { HistoryInfoCards } from './cards/HistoryInfoCards';
import { SupervisionAndInvoiceInfoCards } from './cards/SupervisionAndInvoiceInfoCards';
import { VeterinarianGroomerCard } from './cards/VeterinarianGroomerCard';
import { useOwnerContext } from '../../../../contexts/OwnerContext/OwnerContext';

/**
 * The `DetailsCards` component renders a collection of info cards presenting detailed
 * information about a pet owner including personal details, pet sitting address, animals,
 * ratings, history, and supervision & invoice details. The information is displayed
 * within respective child components and arranged in a grid layout.
 *
 * @component
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.owner - The owner object which contains details about the pet owner including petsitting address and animals.
 * @param {boolean} props.isLoading - A boolean indicating the loading state of the component.
 *
 * @example
 * <DetailsCards owner={ownerData} isLoading={false} />
 *
 * @returns {React.Element} The React component rendered as a collection of detail cards.
 */
export const OwnerShowCards = ({
  isLoading,
  setReviewDrawerOpen,
  form,
  setSupervisionRating,
  setShowReviewDrawer,
  setRefreshData,
  refreshData,
  setReviewData,
  reviewData,
  setEditReviewDrawer
}) => {
  const { owner } = useOwnerContext();
  const { veterinarian, groomer } = owner || {};

  const getColWidth = (count) => {
    if (count === 1) {
      return 24;
    }
    return 12;
  };

  return (
    <Row gutter={[24, 24]}>
      <Col xs={24}>
        <OwnerPersonalInfoCards isLoading={isLoading} />
      </Col>
      {owner?.petsitting_address && (
        <Col xs={24}>
          <PetsittingAddressInfoCards isLoading={isLoading} />
        </Col>
      )}
      {owner?.animals?.length > 0 && (
        <Col xs={24} xxl={24}>
          <AnimalInfoCards isLoading={isLoading} getColWidth={getColWidth} />
        </Col>
      )}
      {(veterinarian || groomer) && (
        <Col xs={24} xxl={24}>
          <VeterinarianGroomerCard
            veterinarian={veterinarian}
            groomer={groomer}
          />
        </Col>
      )}
      <Col xs={24} xxl={24}>
        <RatingInfoCards
          setReviewDrawerOpen={setReviewDrawerOpen}
          form={form}
          setSupervisionRating={setSupervisionRating}
          setShowReviewDrawer={setShowReviewDrawer}
          setRefreshData={setRefreshData}
          refreshData={refreshData}
          setReviewData={setReviewData}
          reviewData={reviewData}
          setEditReviewDrawer={setEditReviewDrawer}
        />
      </Col>
      <Col xs={24} lg={8}>
        <HistoryInfoCards />
      </Col>
      <Col xs={24} lg={16}>
        {Object.keys(owner).length ? <SupervisionAndInvoiceInfoCards /> : null}
      </Col>
    </Row>
  );
};

OwnerShowCards.propTypes = {
  isLoading: PropTypes.bool,
  setReviewDrawerOpen: PropTypes.func,
  form: PropTypes.shape({}).isRequired,
  setSupervisionRating: PropTypes.func,
  setShowReviewDrawer: PropTypes.func,
  setRefreshData: PropTypes.func,
  refreshData: PropTypes.bool,
  setReviewData: PropTypes.func,
  reviewData: PropTypes.shape({}),
  setEditReviewDrawer: PropTypes.func
};

OwnerShowCards.defaultProps = {
  reviewData: {},
  setReviewDrawerOpen: () => {},
  setSupervisionRating: () => {},
  setShowReviewDrawer: () => {},
  setRefreshData: () => {},
  setReviewData: () => {},
  setEditReviewDrawer: () => {},
  refreshData: false,
  isLoading: false
};
